// Inline-SVG chart primitives: LineChart, BarChart, Donut.
//
// Deliberately tiny and dependency-free (no recharts / chart.js): the P&L screen
// needs small spark-style line/bar charts (Feb–Jun series) and an account-status
// donut. Data points are plain numbers (chart axes are presentational); the
// authoritative money figures live in the stat cards as exact strings.
//
// All three accept a `color` CSS value (default the brand token) and are sized
// by their container via width/height props.

import { cn } from "@/lib/utils";
import { type Tone, toneDotClass } from "./tones";

// ---- LineChart --------------------------------------------------------------

export function LineChart({
  data,
  width = 280,
  height = 72,
  color = "var(--nx-brand)",
  className,
}: {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  className?: string;
}) {
  const pad = 4;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const span = max - min || 1;
  const stepX = data.length > 1 ? (width - pad * 2) / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (height - pad * 2) * (1 - (v - min) / span);
    return [x, y] as const;
  });
  const path = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ");
  const area = `${path} L${points.at(-1)?.[0].toFixed(1)} ${height - pad} L${points[0]?.[0].toFixed(1)} ${height - pad} Z`;

  return (
    <svg
      role="img"
      aria-label="line chart"
      viewBox={`0 0 ${width} ${height}`}
      className={cn("h-auto w-full", className)}
      preserveAspectRatio="none"
    >
      <path d={area} fill={color} opacity={0.1} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={1.75} fill={color} />
      ))}
    </svg>
  );
}

// ---- BarChart ---------------------------------------------------------------

export function BarChart({
  data,
  width = 280,
  height = 72,
  color = "var(--nx-profit)",
  className,
}: {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  className?: string;
}) {
  const pad = 4;
  const max = Math.max(...data, 1);
  const n = data.length || 1;
  const gap = 4;
  const barW = (width - pad * 2 - gap * (n - 1)) / n;
  return (
    <svg
      role="img"
      aria-label="bar chart"
      viewBox={`0 0 ${width} ${height}`}
      className={cn("h-auto w-full", className)}
      preserveAspectRatio="none"
    >
      {data.map((v, i) => {
        const h = (height - pad * 2) * (v / max);
        const x = pad + i * (barW + gap);
        const y = height - pad - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={Math.max(h, 1)}
            rx={1.5}
            fill={color}
          />
        );
      })}
    </svg>
  );
}

// ---- Donut ------------------------------------------------------------------

export interface DonutSlice {
  label: string;
  value: number;
  tone: Tone;
}

export function Donut({
  slices,
  size = 132,
  thickness = 16,
  className,
}: {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
  className?: string;
}) {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  let offset = 0;

  return (
    <div className={cn("flex items-center gap-4", className)}>
      <svg
        role="img"
        aria-label="donut chart"
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        className="shrink-0"
      >
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke="var(--nx-border)"
          strokeWidth={thickness}
        />
        {slices.map((s, i) => {
          const frac = s.value / total;
          const dash = frac * c;
          const el = (
            <circle
              key={i}
              cx={cx}
              cy={cx}
              r={r}
              fill="none"
              stroke={`var(--nx-${s.tone})`}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cx})`}
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <ul className="grid gap-1 text-xs">
        {slices.map((s) => (
          <li key={s.label} className="flex items-center gap-2 text-nx-text-2">
            <span
              className={cn("size-2 rounded-full", toneDotClass[s.tone])}
            />
            <span className="capitalize">{s.label}</span>
            <span className="ml-auto font-mono tabular-nums text-nx-text">
              {s.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
